import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import {
  getVendorToken,
  getVendorRefreshToken,
  setVendorTokens,
  clearVendorSession,
} from './vendorAuth';

/**
 * Vendor realm axios instance (TASK-144) — a SEPARATE client from the staff `api`.
 * It attaches ONLY the vendor token and refreshes against `/vendor/auth/refresh`,
 * so a vendor token can never leak onto a staff endpoint. No staff file imports
 * this, and this imports nothing from `lib/api.ts` — the realms don't cross.
 */
const _apiBase = import.meta.env.VITE_API_URL;
const _baseURL = _apiBase ? `${_apiBase}/v1` : '/v1';

export const vendorApi = axios.create({
  baseURL: _baseURL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

vendorApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getVendorToken();
  if (token && config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string) => void;
  reject: (reason: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach(({ resolve, reject }) =>
    error ? reject(error) : token ? resolve(token) : undefined,
  );
  failedQueue = [];
}

vendorApi.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const status = error.response?.status;
    if (status !== 401 || original._retry || !error.response) {
      return Promise.reject(error);
    }
    // Never refresh the auth endpoints themselves.
    if (
      original.url?.includes('/vendor/auth/login') ||
      original.url?.includes('/vendor/auth/refresh') ||
      original.url?.includes('/vendor/auth/accept-invite')
    ) {
      if (original.url?.includes('/vendor/auth/refresh')) {
        clearVendorSession();
        window.location.href = '/vendor/login';
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (original.headers) original.headers['Authorization'] = `Bearer ${token}`;
        return vendorApi(original);
      });
    }

    original._retry = true;
    isRefreshing = true;
    const refreshToken = getVendorRefreshToken();
    if (!refreshToken) {
      clearVendorSession();
      window.location.href = '/vendor/login';
      return Promise.reject(error);
    }
    try {
      const { data } = await axios.post<{
        accessToken: string;
        refreshToken: string;
      }>(`${_baseURL}/vendor/auth/refresh`, { refreshToken });
      setVendorTokens(data.accessToken, data.refreshToken);
      processQueue(null, data.accessToken);
      if (original.headers) {
        original.headers['Authorization'] = `Bearer ${data.accessToken}`;
      }
      return vendorApi(original);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearVendorSession();
      window.location.href = '/vendor/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);
